/* eslint-disable react/prop-types */
import React from 'react';
import {
    reportRenderError
} from './reportRenderError';
import {
    AppErrorFallback, RouteErrorFallback, PanelErrorFallback
} from './fallbacks';

const FALLBACKS = {
    app: AppErrorFallback,
    route: RouteErrorFallback,
    panel: PanelErrorFallback
};

function keysChanged(previous, next) {
    const a = previous || [];
    const b = next || [];
    if (a.length !== b.length) return true;
    return a.some((value, index) => !Object.is(value, b[index]));
}

/**
 * The one error boundary.
 *
 * React 18 unmounts the whole tree on an uncaught render error, so without a
 * boundary a single malformed record — an opcode step, a channel doc, an api
 * definition with the wrong `paramsDef` shape — blanks the entire platform.
 * This catches at whatever depth it is mounted and renders a fallback in the
 * failed subtree's place, leaving every sibling mounted and interactive.
 *
 * Props:
 *   name        {string}   boundary id used in logs (required in practice)
 *   variant     {'app'|'route'|'panel'} which fallback to render
 *   recordType  {string}   e.g. 'api', 'channel', 'opcode' — for the caption
 *   recordId    {string}   the stored record being rendered when it threw
 *   recordLabel {string}   human-readable name for that record
 *   title       {string}   panel-variant heading override
 *   resetKeys   {any[]}    when any value changes, the error state clears
 *   onReset     {Function} called after a manual or key-driven reset
 *   onError     {Function} extra caller-side hook, after reporting
 *   fallback    {node|Function} full override; a function receives
 *               { error, errorInfo, reset, recordType, recordId, recordLabel }
 *
 * Note: this catches render, lifecycle and constructor errors only. Errors
 * thrown in event handlers or async callbacks never reach a boundary — those
 * still need their own try/catch.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            error: null,
            errorInfo: null
        };
        this.reset = this.reset.bind(this);
    }

    static getDerivedStateFromError(error) {
        return {
            error
        };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            errorInfo
        });
        reportRenderError({
            error,
            errorInfo,
            boundary: this.props.name || this.props.variant || 'ErrorBoundary',
            recordType: this.props.recordType,
            recordId: this.props.recordId,
            recordLabel: this.props.recordLabel
        });
        if (typeof this.props.onError === 'function') {
            try {
                this.props.onError(error, errorInfo);
            } catch (hookError) {
                // A caller-side hook must not escalate the crash it was told about.
            }
        }
    }

    componentDidUpdate(prevProps) {
        // Navigating away, or selecting a different record, should clear the
        // error — otherwise one bad row poisons the panel for the whole session.
        if (!this.state.error) return;
        if (keysChanged(prevProps.resetKeys, this.props.resetKeys)) this.reset();
    }

    reset() {
        this.setState({
            error: null,
            errorInfo: null
        });
        if (typeof this.props.onReset === 'function') this.props.onReset();
    }

    render() {
        const {
            error, errorInfo
        } = this.state;
        if (!error) return this.props.children;

        const {
            fallback, variant, title, recordType, recordId, recordLabel
        } = this.props;
        const args = {
            error,
            errorInfo,
            reset: this.reset,
            title,
            recordType,
            recordId,
            recordLabel
        };

        if (typeof fallback === 'function') return fallback(args);
        if (fallback !== undefined && fallback !== null) return fallback;

        const Fallback = FALLBACKS[variant] || PanelErrorFallback;
        return <Fallback {...args} />;
    }
}
